import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { TimesheetsService } from './timesheet.service';
import { DailyTimeEntryModel, TimesheetModel } from './timesheet.models';
import { User } from '@sentry/angular';

/** This file is divided in 3 parts:
 * - The definition of the Store : defining data structure
 * - Constants that allow NgXs to trigger Actions
 * - Selectors & Actions : Functions allowed to interact with the store
 *
 */

// Store definition
export interface TimesheetsStateModel {
    timesheets: TimesheetModel[];
    uncompletedWeeks: Date[];
    isLoading: boolean;
    from: Date | undefined;
    to: Date | undefined;
    userId: string | undefined;
    contributor: User | undefined;
    contributorEntries: DailyTimeEntryModel[];
}

//Constants

export class GetTimesheets {
    static readonly type = 'TIMESHEETS/ALL';
    constructor(public payload: { userId?: string; from?: Date }) {}
}

export class GetUncompletedWeeks {
    static readonly type = 'TIMESHEETS/UNCOMPLETEDWEEKS';
    constructor(public payload: { userId?: string }) {}
}

export class UpdateTimesheets {
    static readonly type = 'TIMESHEETS/UPDATE';
    constructor(public payload: { timesheets: TimesheetModel[] }) {}
}

export class GetContributor {
    static readonly type = 'PROJECTS/CONTRIBUTOR/GET';
    constructor(public payload: { userId: string }) {}
}

//Actions
@State<TimesheetsStateModel>({
    name: 'Timesheets',
    defaults: {
        timesheets: [],
        uncompletedWeeks: [],
        isLoading: false,
        from: undefined,
        to: undefined,
        userId: undefined,

        contributor: undefined,
        contributorEntries: [],
    },
})
@Injectable()
export class TimesheetsState {
    @Selector()
    public static timesheets(state: TimesheetsStateModel): TimesheetModel[] | null {
        return state.timesheets;
    }
    @Selector()
    public static uncompletedWeeks(state: TimesheetsStateModel): Date[] | null {
        return state.uncompletedWeeks;
    }

    @Selector()
    public static isLoading(state: TimesheetsStateModel): boolean {
        return state.isLoading;
    }
    @Selector()
    public static range(
        state: TimesheetsStateModel,
    ): { from: Date | undefined; to: Date | undefined } | undefined {
        return { from: state.from, to: state.to };
    }

    @Selector()
    public static contributor(state: TimesheetsStateModel): User | undefined {
        return state.contributor;
    }

    @Selector()
    public static contributorEntries(state: TimesheetsStateModel): DailyTimeEntryModel[] | null {
        return state.contributorEntries;
    }

    constructor(
        private timesheetsService: TimesheetsService,
        private readonly store: Store,
    ) {}

    @Action(GetTimesheets)
    public async getTimesheets(
        ctx: StateContext<TimesheetsStateModel>,
        action: GetTimesheets,
    ): Promise<void> {
        ctx.patchState({ isLoading: true, userId: undefined, timesheets: [] });
        let timesheets: TimesheetModel[];
        const userId = action.payload.userId;
        let from: Date;
        let to: Date;
        if (action.payload.from) {
            from = mondayOf(action.payload.from);
            to = fridayOfWeek(from);
            timesheets = await this.timesheetsService.getTimesheetsBetween(
                from,
                to,
                action.payload.userId,
            );
        } else {
            timesheets = await this.timesheetsService.getLastTimesheets();
            timesheets = timesheets.map((t) => ({ ...t, date: new Date(t.date) }));
            from = new Date(
                timesheets.reduce(
                    (acc, elem) => Math.min(acc, elem.date.getTime()),
                    timesheets[0].date.getTime(),
                ),
            );
            to = new Date(
                timesheets.reduce(
                    (acc, elem) => Math.max(acc, elem.date.getTime()),
                    timesheets[0].date.getTime(),
                ),
            );
        }
        timesheets = timesheets.map((t) => ({
            ...t,
            date: new Date(t.date),
            dailyTimeEntries: t.dailyTimeEntries.map((d) => ({
                ...d,
                date: new Date(d.date),
                createdAt: new Date(d.createdAt),
                updatedAt: new Date(d.updatedAt),
                percentOfDay: Number(d.percentOfDay),
            })),
        }));
        ctx.patchState({ timesheets, from, to, userId, isLoading: false });
    }

    @Action(GetUncompletedWeeks)
    public async getUncompletedWeeks(
        ctx: StateContext<TimesheetsStateModel>,
        action: GetUncompletedWeeks,
    ): Promise<void> {
        ctx.patchState({ isLoading: true, uncompletedWeeks: [] });
        const weeks = await this.timesheetsService.getUncompletedweeks();
        ctx.patchState({ isLoading: false, uncompletedWeeks: weeks.map((w) => new Date(w)) });
    }

    @Action(UpdateTimesheets)
    public async updateTimesheets(
        ctx: StateContext<TimesheetsStateModel>,
        action: UpdateTimesheets,
    ): Promise<void> {
        try {
            const timesheet = action.payload.timesheets;
            const entries: DailyTimeEntryModel[] = timesheet.reduce(
                (acc: DailyTimeEntryModel[], timesheet: TimesheetModel) => [
                    ...acc,
                    ...timesheet.dailyTimeEntries,
                ],
                [],
            );

            await this.timesheetsService.updateTimesheets(entries);
            const currentState = ctx.getState();
            this.store.dispatch(new GetTimesheets({ userId: currentState.userId }));
        } catch (err: any) {
            console.log(err);
        }
    }

    @Action(GetContributor)
    public async getContributor(
        ctx: StateContext<TimesheetsStateModel>,
        action: GetContributor,
    ): Promise<void> {
        ctx.patchState({ isLoading: true, contributor: undefined, contributorEntries: [] });
        const { user, dailyTimeEntries } = await this.timesheetsService.getContributor(
            action.payload.userId,
        );
        ctx.patchState({
            isLoading: false,
            contributor: user,
            contributorEntries: dailyTimeEntries,
        });
    }
}

function addDays(d: Date, n: number): Date {
    const dt = new Date(d); // local
    dt.setDate(dt.getDate() + n);
    return dt;
}

export function mondayOf(d: Date): Date {
    const dt = new Date(d);
    dt.setHours(0, 0, 0, 0);
    const day = (dt.getDay() + 6) % 7; // 0=Lundi ... 6=Dimanche
    dt.setDate(d.getDate() - day);
    return dt;
}

function fridayOfWeek(weekStart: Date): Date {
    return addDays(weekStart, 5);
}

export function formatDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
